'use client'

import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import {
  ClockIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useRouter, useSearchParams } from 'next/navigation'
import { Fragment, useEffect, useState } from 'react'

const MAX_RECENT_SEARCHES = 5

export function SearchModal({ isOpen, onClose }) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [recentSearches, setRecentSearches] = useState([])

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('pano_recent_searches')
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved))
      } catch (e) {
        console.error('Failed to parse recent searches:', e)
      }
    }
  }, [])

  // Save a search to recent searches
  const saveRecentSearch = (query) => {
    if (!query.trim()) return

    const trimmedQuery = query.trim()

    // Remove duplicates and add to front
    const updated = [
      trimmedQuery,
      ...recentSearches.filter((s) => s !== trimmedQuery),
    ].slice(0, MAX_RECENT_SEARCHES)

    setRecentSearches(updated)
    localStorage.setItem('pano_recent_searches', JSON.stringify(updated))
  }

  // Clear all recent searches
  const clearRecentSearches = () => {
    setRecentSearches([])
    localStorage.removeItem('pano_recent_searches')
  }

  // Handle search submission
  const handleSearch = (query) => {
    const trimmedQuery = query.trim()
    if (!trimmedQuery) return

    saveRecentSearch(trimmedQuery)

    // Build new URL with search param + existing params + expanded time window
    const params = new URLSearchParams(searchParams)
    params.set('search', trimmedQuery)

    // Expand time window to 1 week (168 hours) for searches if not already set
    if (!params.has('window')) {
      params.set('window', '168')
    }

    // Set sort to most_recent for searches if not already set
    if (!params.has('sort')) {
      params.set('sort', 'most_recent')
    }

    // Reset to first page
    params.delete('size')
    params.delete('cursor')

    router.push(`?${params.toString()}`)
    onClose()
    setSearchQuery('')
  }

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault()
    handleSearch(searchQuery)
  }

  // Handle recent search click
  const handleRecentSearchClick = (query) => {
    handleSearch(query)
  }

  // Handle modal close
  const handleClose = () => {
    setSearchQuery('')
    onClose()
  }

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={handleClose} className="relative z-50">
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm" />
        </TransitionChild>

        {/* Modal Container */}
        <div className="fixed inset-0 flex items-start justify-center p-4 pt-[20vh]">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="bg-background border-2 border-foreground w-full max-w-2xl rounded-sm shadow-2xl">
              {/* Header */}
              <div className="border-b-2 border-foreground px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MagnifyingGlassIcon className="h-6 w-6 text-foreground" />
                  <h2 className="font-serif text-2xl font-bold tracking-tight">
                    Search Stories
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-sm p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label="Close search"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Search Form */}
              <div className="p-6">
                <form onSubmit={handleSubmit}>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search for topics, keywords, or stories..."
                      className="w-full border-2 border-border bg-background px-4 py-3 pr-12 font-sans text-base text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none focus:ring-2 focus:ring-ring rounded-sm"
                      autoFocus
                    />
                    <button
                      type="submit"
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      aria-label="Search"
                    >
                      <MagnifyingGlassIcon className="h-5 w-5" />
                    </button>
                  </div>
                </form>

                {/* Search Info */}
                <p className="mt-3 text-sm text-muted-foreground font-sans">
                  Searches will look across stories from the last week, sorted
                  by most recent
                </p>
              </div>

              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <div className="border-t border-border px-6 py-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="byline">Recent Searches</span>
                    </div>
                    <button
                      type="button"
                      onClick={clearRecentSearches}
                      className="byline text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Clear All
                    </button>
                  </div>

                  <div className="space-y-2">
                    {recentSearches.map((query, index) => (
                      <button
                        key={index}
                        onClick={() => handleRecentSearchClick(query)}
                        className="w-full text-left px-3 py-2 font-sans text-sm text-foreground hover:bg-muted transition-colors border border-border rounded-sm"
                      >
                        {query}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
